import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Text } from '@heartinternet/hi-ui';
import { Link } from '@heartinternet/hi-ui';
import { Notice } from '@heartinternet/hi-ui';

import { CopyButton } from '@heartinternet/hi-ui';
import { Icon } from '@heartinternet/hi-ui';
import type { IconName } from '@heartinternet/hi-ui';
import { Tooltip } from '@heartinternet/hi-ui';
import { Table } from '@heartinternet/hi-ui';
import type { Column } from '@heartinternet/hi-ui';
import { DomainsModal } from './components/DomainsModal';
import { AssignDomainModal } from './components/AssignDomainModal';
import { UpgradeModal } from './components/UpgradeModal';
import { useUpgradeNotifications } from './useUpgradeNotifications';
import { PackageManageMenu } from './components/PackageManageMenu';
import { fetchJson, postJson, patchJson } from '@heartinternet/hi-ui';
import type { PackageTablePageProps, Service, ServiceDomain, AssignableDomain } from './types';
import {
  formatType,
  stripProductPrefix,
  formatDate,
  getPrimaryDomain,
  sortServices,
} from './utils';
import './PackageTablePage.css';

export type { PackageTablePageProps };

const SERVICES_ENDPOINT = '/cp/api/v1/services/';
const DOMAINS_ENDPOINT = '/cp/api/v1/domains/hosting';

export const PackageTablePage: React.FC<PackageTablePageProps> = ({
  serviceType,
  csrfToken,
  showDnsUpdateFeature = false,
  showUpgrades = false,
  trustpilotToken,
  trustpilotTemplateId,
  trustpilotBusinessUnitId,
}) => {
  const isWordpress = serviceType === 'managed-wordpress';
  const apiPath = `${SERVICES_ENDPOINT}${serviceType}`;
  const title = isWordpress ? 'Your Managed WordPress Packages' : 'Your cPanel Packages';

  const [services, setServices] = useState<Service[]>([]);
  const [assignableDomains, setAssignableDomains] = useState<AssignableDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [domainsModal, setDomainsModal] = useState<{
    isOpen: boolean;
    domains: ServiceDomain[];
    primaryDomain: string;
    ipAddress: string | null;
    packageType: string;
  }>({ isOpen: false, domains: [], primaryDomain: '', ipAddress: null, packageType: '' });

  const [assignModal, setAssignModal] = useState<{
    isOpen: boolean;
    serviceId: string;
    isReassign: boolean;
    currentDomain?: string;
  }>({ isOpen: false, serviceId: '', isReassign: false });

  const [upgradeModal, setUpgradeModal] = useState<{
    isOpen: boolean;
    serviceId: string;
    currentPlanType: string;
    renewalDate: string;
  }>({
    isOpen: false,
    serviceId: '',
    currentPlanType: '',
    renewalDate: '',
  });

  const { notifications, dismissNotification } = useUpgradeNotifications(
    serviceType,
    services,
    csrfToken,
    showUpgrades
  );

  const abortRef = useRef(new AbortController());

  const fetchData = useCallback(async () => {
    abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    const [servicesRes, domainsRes] = await Promise.all([
      fetchJson<{ items: Service[] }>(apiPath, { signal: controller.signal }),
      fetchJson<{ items: AssignableDomain[] }>(DOMAINS_ENDPOINT, { signal: controller.signal }),
    ]);

    const errors = [servicesRes.error, domainsRes.error].filter(Boolean);
    if (errors.length > 0) {
      setError(errors.join(' '));
      setLoading(false);
      return;
    }

    /* v8 ignore start */
    setServices(servicesRes.data?.items || []);
    setAssignableDomains(domainsRes.data?.items || []);
    /* v8 ignore stop */
    setLoading(false);
  }, [apiPath]);

  /* eslint-disable react-hooks/set-state-in-effect -- data fetching effect legitimately sets loading state */
  useEffect(() => {
    void fetchData();
    return () => abortRef.current.abort();
  }, [fetchData]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleAssignFromDropdown = useCallback((serviceId: string) => {
    setAssignModal({ isOpen: true, serviceId, isReassign: false });
  }, []);

  const handleUpgradeFromDropdown = useCallback((row: Service) => {
    setUpgradeModal({
      isOpen: true,
      serviceId: row.id,
      currentPlanType: row.type,
      renewalDate: row.expiry_date ?? '',
    });
  }, []);

  const handleAssignConfirm = useCallback(
    async (
      domainName: string,
      updateDns: boolean
    ): Promise<{ success: boolean; error?: string }> => {
      const { serviceId, isReassign } = assignModal;
      const endpoint = `${apiPath}/${serviceId}/primary-domain`;
      const headers = { 'X-CSRF-Token': csrfToken };
      const res = isReassign
        ? await patchJson(endpoint, { primary_domain: domainName }, headers)
        : await postJson(endpoint, { primary_domain: domainName, update_dns: updateDns }, headers);

      if (res.error) {
        return { success: false, error: res.error };
      }

      await fetchData();
      return { success: true };
    },
    [assignModal, apiPath, csrfToken, fetchData]
  );

  const defaultSorted = useMemo(() => sortServices(services), [services]);
  const [sortKey, setSortKey] = useState<string>('common_name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const sortedServices = useMemo(() => {
    if (sortKey === 'common_name') {
      return sortDirection === 'desc' ? defaultSorted : [...defaultSorted].reverse();
    }

    return [...defaultSorted].sort((a, b) => {
      let aVal: string;
      let bVal: string;

      if (sortKey === 'expiry_date') {
        aVal = a.expiry_date ?? '';
        bVal = b.expiry_date ?? '';
      } else {
        aVal = String((a as unknown as Record<string, unknown>)[sortKey]).toLowerCase();
        bVal = String((b as unknown as Record<string, unknown>)[sortKey]).toLowerCase();
      }

      const cmp = aVal.localeCompare(bVal);
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [defaultSorted, sortKey, sortDirection]);

  const handleSort = useCallback((key: string, direction: 'asc' | 'desc') => {
    setSortKey(key);
    setSortDirection(direction);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const hasAssignableDomains = assignableDomains.length > 0;

  const columns: Column<Service>[] = useMemo(
    () => [
      {
        key: 'common_name',
        label: 'Package Domain',
        sortable: true,
        render: (_value: unknown, row: Service) => {
          const primary = getPrimaryDomain(row);
          const displayName = primary?.domain_name ?? row.common_name;
          return (
            <div className="hi-ui-package-table__domain-cell">
              <Icon
                name={
                  `${isWordpress ? 'wordpress' : 'cpanel'}${row.is_suspended ? '-suspended' : ''}` as IconName
                }
                size={16}
                className="hi-ui-package-table__product-icon"
                aria-label={isWordpress ? 'WordPress' : 'cPanel'}
              />
              <span className="hi-ui-package-table__domain-name">{displayName}</span>
              {!primary && (
                <Tooltip content="To access and manage this package, you'll need to assign a domain. Click 'Assign Domain' to get started.">
                  <Icon
                    name="question-red"
                    size={14}
                    className="hi-ui-package-table__info-icon"
                    aria-label="Unassigned package"
                  />
                </Tooltip>
              )}
              {primary && !isWordpress && !row.is_suspended && (
                <button
                  className="hi-ui-package-table__edit-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAssignModal({
                      isOpen: true,
                      serviceId: row.id,
                      isReassign: true,
                      currentDomain: primary.domain_name,
                    });
                  }}
                  aria-label="Reassign domain"
                >
                  <Icon
                    name="pencil"
                    size={11}
                    className="hi-ui-package-table__edit-icon"
                    aria-label="Edit"
                  />
                </button>
              )}
            </div>
          );
        },
      },
      {
        key: 'type',
        label: 'Type',
        sortable: true,
        render: (value: unknown) => (
          <div className="hi-ui-package-table__package-badge-border">
            <span className="hi-ui-package-table__package-badge">
              {formatType(value as string)}
            </span>
          </div>
        ),
      },
      {
        key: 'domains',
        label: 'Websites',
        render: (_value: unknown, row: Service) => {
          if (!row.domains || row.domains.length === 0) {
            return <span className="hi-ui-package-table__websites-cell">Not assigned</span>;
          }
          const primary = getPrimaryDomain(row);
          return (
            <span className="hi-ui-package-table__websites-cell">
              <Link
                href="#"
                className="hi-ui-package-table__websites-link"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDomainsModal({
                    isOpen: true,
                    domains: row.domains,
                    primaryDomain: primary?.domain_name ?? row.common_name,
                    ipAddress: row.ip,
                    packageType: row.type,
                  });
                }}
              >
                {row.domains.length === 1 ? '1 website' : `${row.domains.length} websites`}
              </Link>
            </span>
          );
        },
      },
      {
        key: 'ip',
        label: 'IP',
        render: (value: unknown, row: Service) => {
          if (row.is_suspended) {
            return (
              <Tooltip content="This package is currently suspended. To resolve this issue and get back online, please contact our support team.">
                <div className="hi-ui-package-table__suspended-badge">
                  <span>Suspended</span>
                  <Icon name="question-white" size={11} />
                </div>
              </Tooltip>
            );
          }
          const ip = value as string | null;
          if (!ip) return null;
          return (
            <div className="hi-ui-package-table__ip-cell">
              <Text variant="body-small" inline>
                {ip}
              </Text>
              <CopyButton text={ip} />
            </div>
          );
        },
      },
      {
        key: 'expiry_date',
        label: 'Renews',
        sortable: true,
        render: (value: unknown) => (
          <Text variant="body-small" inline>
            {formatDate(value as string | null)}
          </Text>
        ),
      },
      {
        key: 'id',
        label: '',
        align: 'right' as const,
        render: (_value: unknown, row: Service) => (
          <PackageManageMenu
            row={row}
            isWordpress={isWordpress}
            apiPath={apiPath}
            csrfToken={csrfToken}
            upgradeEnabled={showUpgrades}
            onError={setError}
            onAssign={handleAssignFromDropdown}
            onUpgrade={handleUpgradeFromDropdown}
          />
        ),
      },
    ],
    [
      isWordpress,
      apiPath,
      csrfToken,
      showUpgrades,
      handleAssignFromDropdown,
      handleUpgradeFromDropdown,
    ]
  );

  return (
    <div className="hi-ui-package-table-page">
      <div className="hi-ui-package-table-page__header">
        <Text variant="heading-1">{title}</Text>

        {!loading && !error && hasAssignableDomains && (
          <div className="hi-ui-package-table-page__intro">
            <Text variant="body">
              To access and manage a package, you&apos;ll need an assigned domain. Click &apos;
              <Text as="span" bold>
                Assign Domain
              </Text>
              &apos; to get started.
            </Text>
            <Text variant="body">
              If you don&apos;t own a domain yet,{' '}
              <Link href="https://www.heartinternet.uk/domain-names" external>
                search for one now
              </Link>
              .
            </Text>
            <Text variant="body">
              Alternatively,{' '}
              <Link href="/manage/transfer.cgi" external>
                transfer an existing domain
              </Link>{' '}
              or{' '}
              <Link href="/manage/soft_transfer.cgi" external>
                create a domain reference
              </Link>
              .
            </Text>
            <Text variant="body">
              Once you have a domain assigned, click &apos;
              <Text as="span" bold>
                Manage
              </Text>
              &apos; to access your package control panel.
            </Text>
          </div>
        )}
      </div>

      {!loading && !error && !hasAssignableDomains && (
        <Notice variant="error" className="hi-ui-package-table-page__no-domains">
          <Text variant="body" bold>
            No domains found
          </Text>
          <div className="hi-ui-package-table-page__errorbox-body">
            <Text variant="body" bold>
              You do not have any domains in your Heart Internet account.
            </Text>
            <Text variant="body">
              If you have a domain name with another provider you can{' '}
              <Link href="/manage/transfer.cgi" external>
                transfer your domain to Heart
              </Link>
              .
            </Text>
            <Text variant="body">
              Don&apos;t currently own a domain name?{' '}
              <Link href="https://www.heartinternet.uk/domain-names" external>
                Search for your perfect domain now
              </Link>
              .
            </Text>
            <Text variant="body">
              For a temporary solution, you can{' '}
              <Link href="/manage/soft_transfer.cgi" external>
                create a domain reference
              </Link>
              .
            </Text>
          </div>
        </Notice>
      )}

      {error && (
        <Notice variant="error" dismissible icon={<Icon name="error-circle" size={26} />}>
          <div className="hi-ui-package-table-page__notification-error">
            <Text
              variant="body"
              bold
              inline
              className="hi-ui-package-table-page__notification-error-title"
            >
              Something went wrong
            </Text>
            <Text variant="body-small" inline>
              {error}
            </Text>
          </div>
        </Notice>
      )}

      {notifications.length > 0 && (
        <div className="hi-ui-package-table-page__notifications">
          {notifications.map((n) => (
            <Notice
              key={n.serviceId}
              variant={n.type === 'error' ? 'error' : 'success'}
              dismissible={n.type !== 'in_progress'}
              onDismiss={() => dismissNotification(n.serviceId)}
              icon={
                n.type === 'in_progress' ? (
                  <span className="hi-ui-package-table-page__pulse-icon">
                    <Icon name="success-circle" size={26} />
                  </span>
                ) : n.type === 'complete' ? (
                  <Icon name="success-circle" size={26} />
                ) : (
                  <Icon name="error-circle" size={26} />
                )
              }
            >
              {n.type === 'in_progress' && (
                <div className="hi-ui-package-table-page__notification-success">
                  <Text variant="body" bold inline>
                    Upgrade in progress...
                  </Text>
                  <Text variant="body-small" inline>
                    {n.domain} is being upgraded to the {stripProductPrefix(n.toName)} package
                  </Text>
                </div>
              )}
              {n.type === 'complete' && (
                <div className="hi-ui-package-table-page__notification-success">
                  <Text variant="body" bold inline>
                    Upgrade complete!
                  </Text>
                  <Text variant="body-small" inline>
                    {n.domain} has been upgraded to the {stripProductPrefix(n.toName)} package
                  </Text>
                </div>
              )}
              {n.type === 'error' && (
                <div className="hi-ui-package-table-page__notification-error">
                  <Text variant="body" bold inline>
                    We couldn&apos;t upgrade {n.domain}
                  </Text>
                  <Text variant="body-small" inline>
                    Your payment was declined, check your details and try again.{' '}
                    <Link href="https://heartinternet.uk/support/" external>
                      Contact support
                    </Link>
                  </Text>
                </div>
              )}
            </Notice>
          ))}
        </div>
      )}

      <Table<Service>
        columns={columns}
        data={sortedServices.slice((currentPage - 1) * pageSize, currentPage * pageSize)}
        loading={loading}
        loadingMessage="Loading packages..."
        emptyMessage="No packages found."
        gridColumns="1fr auto auto auto auto auto"
        sortKey={sortKey}
        sortDirection={sortDirection}
        onSort={handleSort}
        pagination={{
          currentPage,
          pageSize,
          totalItems: sortedServices.length,
          pageSizeOptions: [10, 25, 50],
        }}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      <DomainsModal
        isOpen={domainsModal.isOpen}
        onClose={() => setDomainsModal((prev) => ({ ...prev, isOpen: false }))}
        domains={domainsModal.domains}
        primaryDomain={domainsModal.primaryDomain}
        ipAddress={domainsModal.ipAddress}
        packageType={domainsModal.packageType}
        isWordpress={isWordpress}
      />

      <AssignDomainModal
        isOpen={assignModal.isOpen}
        onClose={() => setAssignModal((prev) => ({ ...prev, isOpen: false }))}
        isReassign={assignModal.isReassign}
        currentDomain={assignModal.currentDomain}
        assignableDomains={assignableDomains}
        showDnsUpdateFeature={showDnsUpdateFeature}
        onConfirm={handleAssignConfirm}
      />

      <UpgradeModal
        isOpen={upgradeModal.isOpen}
        onClose={() => setUpgradeModal((prev) => ({ ...prev, isOpen: false }))}
        serviceId={upgradeModal.serviceId}
        serviceType={serviceType}
        currentPlanType={upgradeModal.currentPlanType}
        renewalDate={upgradeModal.renewalDate}
        isWordpress={isWordpress}
        trustpilotToken={trustpilotToken}
        trustpilotTemplateId={trustpilotTemplateId}
        trustpilotBusinessUnitId={trustpilotBusinessUnitId}
      />
    </div>
  );
};
